import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bike, ShieldCheck, Star, Eye, Ban, CheckCircle, FileText, AlertTriangle, Wifi, WifiOff, Smartphone, Link, X, MapPin, Calendar, Award, TrendingUp, Package, Power, ToggleLeft, ToggleRight } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
const initialRiders = [{
  id: 1,
  name: 'John Doe',
  email: 'john@example.com',
  phone: '+1 234 567 8900',
  status: 'online',
  verification: 'verified',
  rating: 4.8,
  device: 'approved',
  bike: 'BK-1001',
  compliance: 'compliant',
  joined: 'Dec 1, 2023',
  totalRides: 342,
  earnings: '₦45,600',
  acceptanceRate: '94%',
  lastLocation: 'Victoria Island, Lagos',
  completionRate: '98%'
}, {
  id: 2,
  name: 'Mike Ross',
  email: 'mike@example.com',
  phone: '+1 234 567 8901',
  status: 'offline',
  verification: 'verified',
  rating: 4.9,
  device: 'approved',
  bike: 'BK-1002',
  compliance: 'compliant',
  joined: 'Jan 15, 2024',
  totalRides: 189,
  earnings: '₦28,400',
  acceptanceRate: '96%',
  lastLocation: 'Lekki Phase 1',
  completionRate: '99%'
}, {
  id: 3,
  name: 'Sarah Connor',
  email: 'sarah@example.com',
  phone: '+1 234 567 8902',
  status: 'pending',
  verification: 'pending',
  rating: 0,
  device: 'pending',
  bike: 'Unassigned',
  compliance: 'pending',
  joined: 'Mar 10, 2024',
  totalRides: 0,
  earnings: '₦0',
  acceptanceRate: 'N/A',
  lastLocation: 'N/A',
  completionRate: 'N/A'
}, {
  id: 4,
  name: 'Bruce Wayne',
  email: 'bruce@example.com',
  phone: '+1 234 567 8903',
  status: 'suspended',
  verification: 'verified',
  rating: 4.2,
  device: 'approved',
  bike: 'Unassigned',
  compliance: 'expired_license',
  joined: 'Feb 5, 2024',
  totalRides: 67,
  earnings: '₦8,900',
  acceptanceRate: '78%',
  lastLocation: 'Ikeja GRA',
  completionRate: '85%'
}, {
  id: 5,
  name: 'Clark Kent',
  email: 'clark@example.com',
  phone: '+1 234 567 8904',
  status: 'online',
  verification: 'verified',
  rating: 5.0,
  device: 'approved',
  bike: 'BK-1005',
  compliance: 'compliant',
  joined: 'Nov 20, 2023',
  totalRides: 456,
  earnings: '₦62,100',
  acceptanceRate: '99%',
  lastLocation: 'Ikoyi',
  completionRate: '100%'
}];
const deviceQueue = [{
  id: 1,
  rider: 'Sarah Connor',
  model: 'Samsung Galaxy S21',
  imei: '354829103847291',
  date: 'Today, 9:30 AM'
}, {
  id: 2,
  rider: 'Barry Allen',
  model: 'iPhone 13',
  imei: '990012847291038',
  date: 'Yesterday'
}];
export function RiderManagement() {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedRider, setSelectedRider] = useState<any>(null);
  const [riders, setRiders] = useState(initialRiders);
  const [showBulkConfirm, setShowBulkConfirm] = useState<'online' | 'offline' | null>(null);
  const onlineCount = riders.filter(r => r.status === 'online').length;
  const offlineCount = riders.filter(r => r.status === 'offline').length;
  const eligibleForToggle = riders.filter(r => r.status === 'online' || r.status === 'offline');
  const toggleRiderStatus = (riderId: number) => {
    setRiders(prev => prev.map(r => {
      if (r.id === riderId) {
        if (r.status === 'online') return {
          ...r,
          status: 'offline'
        };
        if (r.status === 'offline') return {
          ...r,
          status: 'online'
        };
      }
      return r;
    }));
  };
  const setAllRidersStatus = (status: 'online' | 'offline') => {
    setRiders(prev => prev.map(r => {
      if (r.status === 'online' || r.status === 'offline') {
        return {
          ...r,
          status
        };
      }
      return r;
    }));
    setShowBulkConfirm(null);
  };
  const columns = [{
    header: 'Rider Profile',
    accessor: (row: any) => <div className="flex items-center gap-3" data-id="element-1073">
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600 relative" data-id="element-1074">
            {row.name.charAt(0)}
            <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${row.status === 'online' ? 'bg-green-500' : row.status === 'offline' ? 'bg-slate-400' : 'bg-yellow-500'}`} data-id="element-1075"></div>
          </div>
          <div data-id="element-1076">
            <p className="font-medium text-slate-900" data-id="element-1077">{row.name}</p>
            <p className="text-xs text-slate-500" data-id="element-1078">{row.email}</p>
          </div>
        </div>
  }, {
    header: 'Status',
    accessor: (row: any) => <div className="flex items-center gap-2" data-id="element-1079">
          <StatusBadge status={row.status} data-id="element-1080" />
          {(row.status === 'online' || row.status === 'offline') && <button onClick={e => {
        e.stopPropagation();
        toggleRiderStatus(row.id);
      }} className={`p-1 rounded transition-colors ${row.status === 'online' ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`} title={row.status === 'online' ? 'Set Offline' : 'Set Online'} data-id="element-1081">
              {row.status === 'online' ? <ToggleRight size={20} data-id="element-1082" /> : <ToggleLeft size={20} data-id="element-1083" />}
            </button>}
        </div>
  }, {
    header: 'Device',
    accessor: (row: any) => <div className="flex items-center gap-1" data-id="element-1084">
          <Smartphone size={14} className="text-slate-400" data-id="element-1085" />
          <span className={`text-sm ${row.device === 'approved' ? 'text-green-600' : 'text-yellow-600'}`} data-id="element-1086">
            {row.device}
          </span>
        </div>
  }, {
    header: 'Bike',
    accessor: (row: any) => <span className={row.bike === 'Unassigned' ? 'text-slate-400 italic' : 'font-mono text-slate-700'} data-id="element-1087">
          {row.bike}
        </span>
  }, {
    header: 'Compliance',
    accessor: (row: any) => <div className="flex items-center gap-1.5" data-id="element-1088">
          {row.compliance === 'compliant' ? <ShieldCheck size={16} className="text-green-500" data-id="element-1089" /> : row.compliance === 'pending' ? <FileText size={16} className="text-yellow-500" data-id="element-1090" /> : <AlertTriangle size={16} className="text-red-500" data-id="element-1091" />}
          <span className="capitalize text-slate-600 text-xs" data-id="element-1092">
            {row.compliance.replace('_', ' ')}
          </span>
        </div>
  }, {
    header: 'Rating',
    accessor: (row: any) => <div className="flex items-center gap-1" data-id="element-1093">
          <Star size={14} className="fill-yellow-400 text-yellow-400" data-id="element-1094" />
          <span className="font-medium text-slate-700" data-id="element-1095">
            {row.rating > 0 ? row.rating : 'N/A'}
          </span>
        </div>
  }];
  const filteredData = activeTab === 'all' ? riders : riders.filter(r => {
    if (activeTab === 'online') return r.status === 'online';
    if (activeTab === 'pending') return r.status === 'pending';
    if (activeTab === 'suspended') return r.status === 'suspended';
    return true;
  });
  return <div className="space-y-6" data-id="element-1096">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1097">
        <StatsCard title="Total Riders" value="1,247" trend={12} icon={Bike} color="gold" delay={0} data-id="element-1098" />
        <StatsCard title="Pending Approval" value="48" trend={-2} icon={FileText} color="purple" delay={0.1} data-id="element-1099" />
        <StatsCard title="Online Now" value={String(onlineCount)} trend={5} icon={Wifi} color="green" delay={0.2} data-id="element-1100" />
        <StatsCard title="Suspended" value="12" trend={-5} icon={Ban} color="blue" delay={0.3} data-id="element-1101" />
      </div>

      {/* Bulk Status Control Panel */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100" data-id="element-1102">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1103">
          <div data-id="element-1104">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2" data-id="element-1105">
              <Power size={18} className="text-famo-gold" data-id="element-1106" />
              Rider Status Control
            </h3>
            <p className="text-xs text-slate-500 mt-1" data-id="element-1107">
              Toggle individual riders or use bulk actions. Only verified riders
              (online/offline) can be toggled.
            </p>
          </div>

          <div className="flex items-center gap-3" data-id="element-1108">
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg" data-id="element-1109">
              <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-1110"></span>
              <span className="font-medium" data-id="element-1111">{onlineCount}</span> Online
              <span className="mx-1 text-slate-300" data-id="element-1112">|</span>
              <span className="w-2 h-2 rounded-full bg-slate-400" data-id="element-1113"></span>
              <span className="font-medium" data-id="element-1114">{offlineCount}</span> Offline
            </div>

            <button onClick={() => setShowBulkConfirm('online')} disabled={offlineCount === 0} className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1115">
              <Wifi size={16} data-id="element-1116" />
              Set All Online
            </button>
            <button onClick={() => setShowBulkConfirm('offline')} disabled={onlineCount === 0} className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1117">
              <WifiOff size={16} data-id="element-1118" />
              Set All Offline
            </button>
          </div>
        </div>

        {/* Bulk Confirmation */}
        <AnimatePresence data-id="element-1119">
          {showBulkConfirm && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden" data-id="element-1120">
              <div className={`mt-4 p-4 rounded-lg border flex items-center justify-between ${showBulkConfirm === 'online' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-1121">
                <div className="flex items-center gap-3" data-id="element-1122">
                  <AlertTriangle size={20} className={showBulkConfirm === 'online' ? 'text-green-600' : 'text-red-600'} data-id="element-1123" />
                  <div data-id="element-1124">
                    <p className="font-medium text-slate-900" data-id="element-1125">
                      {showBulkConfirm === 'online' ? `Set all ${offlineCount} offline rider${offlineCount !== 1 ? 's' : ''} to Online?` : `Set all ${onlineCount} online rider${onlineCount !== 1 ? 's' : ''} to Offline?`}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1126">
                      This will{' '}
                      {showBulkConfirm === 'online' ? 'make riders available for job assignments' : 'remove riders from active dispatch'}
                      . Pending and suspended riders are not affected.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1127">
                  <button onClick={() => setShowBulkConfirm(null)} className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-1128">
                    Cancel
                  </button>
                  <button onClick={() => setAllRidersStatus(showBulkConfirm)} className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${showBulkConfirm === 'online' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-1129">
                    Confirm
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-1130">
        {/* LEFT COLUMN: Rider List */}
        <div className="lg:col-span-3 space-y-6" data-id="element-1131">
          <div className="flex border-b border-slate-200" data-id="element-1132">
            <button onClick={() => setActiveTab('all')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'all' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1133">
              All Riders
            </button>
            <button onClick={() => setActiveTab('online')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'online' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1134">
              Online
              <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full text-xs font-bold" data-id="element-1135">
                {onlineCount}
              </span>
            </button>
            <button onClick={() => setActiveTab('pending')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'pending' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1136">
              Pending
            </button>
            <button onClick={() => setActiveTab('suspended')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'suspended' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1137">
              Suspended
            </button>
          </div>

          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-1138">
            <DataTable title="Rider Directory" data={filteredData} columns={columns} searchPlaceholder="Search riders..." actions={row => <>
                  <button onClick={() => setSelectedRider(row)} className="p-1.5 text-slate-500 hover:text-famo-navy hover:bg-slate-100 rounded transition-colors" title="View Profile" data-id="element-1140">
                    <Eye size={18} data-id="element-1141" />
                  </button>
                  {row.status === 'pending' && <button className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors" title="Approve Application" data-id="element-1142">
                      <CheckCircle size={18} data-id="element-1143" />
                    </button>}
                  {row.status === 'online' && <button onClick={() => toggleRiderStatus(row.id)} className="p-1.5 text-orange-500 hover:bg-orange-50 rounded transition-colors" title="Set Offline" data-id="element-1144">
                      <WifiOff size={18} data-id="element-1145" />
                    </button>}
                  {row.status === 'offline' && <button onClick={() => toggleRiderStatus(row.id)} className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors" title="Set Online" data-id="element-1146">
                      <Wifi size={18} data-id="element-1147" />
                    </button>}
                  <button className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Assign Bike" data-id="element-1148">
                    <Link size={18} data-id="element-1149" />
                  </button>
                  <button className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Suspend Rider" data-id="element-1150">
                    <Ban size={18} data-id="element-1151" />
                  </button>
                </>} data-id="element-1139" />
          </motion.div>
        </div>

        {/* RIGHT COLUMN: Create Form & Device Queue */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1152">
          {/* Create Rider Form */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-1153">
            <h3 className="text-lg font-bold text-slate-800 mb-4" data-id="element-1154">
              Create New Rider
            </h3>
            <div className="space-y-4" data-id="element-1155">
              <div data-id="element-1156">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1157">
                  Full Name
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="e.g. John Doe" data-id="element-1158" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1159">
                <div data-id="element-1160">
                  <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1161">
                    Email
                  </label>
                  <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="john@example.com" data-id="element-1162" />
                </div>
                <div data-id="element-1163">
                  <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1164">
                    Phone
                  </label>
                  <input type="tel" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="+1 234..." data-id="element-1165" />
                </div>
              </div>
              <div data-id="element-1166">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1167">
                  Assigned Bike
                </label>
                <select className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" data-id="element-1168">
                  <option data-id="element-1169">Select a bike...</option>
                  <option data-id="element-1170">BK-1004 (Yamaha Aerox)</option>
                  <option data-id="element-1171">BK-1007 (Honda Beat)</option>
                </select>
              </div>
              <div data-id="element-1172">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1173">
                  Device IMEI
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="Device IMEI number" data-id="element-1174" />
              </div>
              <div data-id="element-1175">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-1176">
                  Notes
                </label>
                <textarea className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none h-20" placeholder="Additional notes..." data-id="element-1177"></textarea>
              </div>
              <button className="w-full bg-famo-gold text-white hover:opacity-90 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-1178">
                Create Rider Account
              </button>
              <p className="text-xs text-slate-400 text-center italic" data-id="element-1179">
                Riders cannot self-register. All accounts must be created by an
                admin.
              </p>
            </div>
          </div>

          {/* Device Approval Queue */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-1180">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-1181">
              <Smartphone size={20} className="text-famo-gold" data-id="element-1182" />
              Device Approval Queue
            </h3>
            <div className="space-y-4" data-id="element-1183">
              {deviceQueue.map(device => <div key={device.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-id="element-1184">
                  <div className="flex justify-between items-start mb-1" data-id="element-1185">
                    <span className="font-medium text-slate-900" data-id="element-1186">
                      {device.rider}
                    </span>
                    <span className="text-xs text-slate-400" data-id="element-1187">
                      {device.date}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-1" data-id="element-1188">{device.model}</p>
                  <p className="text-xs font-mono text-slate-500 mb-3" data-id="element-1189">
                    IMEI: {device.imei}
                  </p>
                  <div className="flex gap-2" data-id="element-1190">
                    <button className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 py-1.5 rounded text-xs font-medium transition-colors" data-id="element-1191">
                      Approve
                    </button>
                    <button className="flex-1 bg-red-50 text-red-700 hover:bg-red-100 py-1.5 rounded text-xs font-medium transition-colors" data-id="element-1192">
                      Reject
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Rider Detail Panel */}
      <AnimatePresence data-id="element-1193">
        {selectedRider && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedRider(null)} data-id="element-1194">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1195">
              {/* Header */}
              <div className="p-6 border-b border-slate-200 flex justify-between items-start bg-slate-50" data-id="element-1196">
                <div className="flex items-center gap-4" data-id="element-1197">
                  <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-2xl font-bold text-slate-600 relative" data-id="element-1198">
                    {selectedRider.name.charAt(0)}
                    <div className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${selectedRider.status === 'online' ? 'bg-green-500' : selectedRider.status === 'offline' ? 'bg-slate-400' : 'bg-yellow-500'}`} data-id="element-1199"></div>
                  </div>
                  <div data-id="element-1200">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-1201">
                      {selectedRider.name}
                    </h2>
                    <p className="text-sm text-slate-500" data-id="element-1202">
                      {selectedRider.email}
                    </p>
                    <div className="mt-1 flex items-center gap-2" data-id="element-1203">
                      <StatusBadge status={selectedRider.status} data-id="element-1204" />
                      <span className="text-xs text-slate-500" data-id="element-1205">
                        • Bike: {selectedRider.bike}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedRider(null)} className="p-2 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-1206">
                  <X size={20} className="text-slate-500" data-id="element-1207" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6" data-id="element-1208">
                {/* Status Toggle in Detail */}
                {(selectedRider.status === 'online' || selectedRider.status === 'offline') && <div className={`p-4 rounded-lg border flex items-center justify-between ${selectedRider.status === 'online' ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-1209">
                    <div className="flex items-center gap-3" data-id="element-1210">
                      {selectedRider.status === 'online' ? <Wifi size={20} className="text-green-600" data-id="element-1211" /> : <WifiOff size={20} className="text-slate-400" data-id="element-1212" />}
                      <div data-id="element-1213">
                        <p className="font-medium text-slate-900" data-id="element-1214">
                          Rider is currently{' '}
                          {selectedRider.status === 'online' ? 'Online' : 'Offline'}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-1215">
                          {selectedRider.status === 'online' ? 'Available for job assignments' : 'Not receiving job assignments'}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => {
                toggleRiderStatus(selectedRider.id);
                const newStatus = selectedRider.status === 'online' ? 'offline' : 'online';
                setSelectedRider({
                  ...selectedRider,
                  status: newStatus
                });
              }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedRider.status === 'online' ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100' : 'bg-green-600 text-white hover:bg-green-700'}`} data-id="element-1216">
                      {selectedRider.status === 'online' ? 'Set Offline' : 'Set Online'}
                    </button>
                  </div>}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1217">
                  <div className="bg-blue-50 p-4 rounded-lg" data-id="element-1218">
                    <Package size={20} className="text-blue-600 mb-2" data-id="element-1219" />
                    <p className="text-2xl font-bold text-slate-900" data-id="element-1220">
                      {selectedRider.totalRides}
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1221">Total Rides</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg" data-id="element-1222">
                    <TrendingUp size={20} className="text-green-600 mb-2" data-id="element-1223" />
                    <p className="text-2xl font-bold text-slate-900" data-id="element-1224">
                      {selectedRider.earnings}
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1225">Total Earnings</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg" data-id="element-1226">
                    <Award size={20} className="text-yellow-600 mb-2" data-id="element-1227" />
                    <p className="text-2xl font-bold text-slate-900" data-id="element-1228">
                      {selectedRider.rating || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1229">Rating</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg" data-id="element-1230">
                    <CheckCircle size={20} className="text-purple-600 mb-2" data-id="element-1231" />
                    <p className="text-2xl font-bold text-slate-900" data-id="element-1232">
                      {selectedRider.acceptanceRate}
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1233">Acceptance Rate</p>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-4" data-id="element-1234">
                  <h3 className="font-bold text-slate-800" data-id="element-1235">Rider Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1236">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-1237">
                      <Calendar size={18} className="text-slate-400" data-id="element-1238" />
                      <div data-id="element-1239">
                        <p className="text-xs text-slate-500" data-id="element-1240">Joined</p>
                        <p className="font-medium text-slate-900" data-id="element-1241">
                          {selectedRider.joined}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-1242">
                      <Smartphone size={18} className="text-slate-400" data-id="element-1243" />
                      <div data-id="element-1244">
                        <p className="text-xs text-slate-500" data-id="element-1245">Device Status</p>
                        <p className="font-medium text-slate-900 capitalize" data-id="element-1246">
                          {selectedRider.device}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-1247">
                      <MapPin size={18} className="text-slate-400" data-id="element-1248" />
                      <div data-id="element-1249">
                        <p className="text-xs text-slate-500" data-id="element-1250">Last Location</p>
                        <p className="font-medium text-slate-900" data-id="element-1251">
                          {selectedRider.lastLocation}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-id="element-1252">
                      <ShieldCheck size={18} className={selectedRider.compliance === 'compliant' ? 'text-green-500' : 'text-red-500'} data-id="element-1253" />
                      <div data-id="element-1254">
                        <p className="text-xs text-slate-500" data-id="element-1255">Compliance</p>
                        <p className="font-medium text-slate-900 capitalize" data-id="element-1256">
                          {selectedRider.compliance.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-slate-200" data-id="element-1257">
                  <button className="flex-1 bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 rounded-lg font-medium transition-colors" data-id="element-1258">
                    Assign Bike
                  </button>
                  <button className="flex-1 bg-green-50 text-green-700 hover:bg-green-100 py-2 rounded-lg font-medium transition-colors" data-id="element-1259">
                    View Ride History
                  </button>
                  <button className="flex-1 bg-red-50 text-red-700 hover:bg-red-100 py-2 rounded-lg font-medium transition-colors" data-id="element-1260">
                    {selectedRider.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}